import { Injectable } from '@angular/core';
import { DataTest, DataTestType, SchemaTestForm } from '@y42/data-test';
import { OperationService } from '@y42/datasource/shared/operation-input';
import { isNullish } from '@y42/util/nullish';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class DataTestConditionFormatterService {
  constructor(private readonly operationService: OperationService) {}

  private readonly conditionTextFormatterDef: Record<
    DataTestType,
    (dataTest: DataTest, schemaTest?: SchemaTestForm | null) => string
  > = {
    [DataTestType.NumRowDrift]: (dataTest) => this.rowDriftFormatter(dataTest),
    [DataTestType.LowCode]: (dataTest) => this.lowCodeFormatter(dataTest),
    [DataTestType.Unique]: (dataTest) => this.uniqueFormatter(dataTest),
    [DataTestType.Column]: (dataTest, schemaTest) => this.columnFormatter(dataTest, schemaTest),
    [DataTestType.Schema]: (dataTest) => this.schemaFormatter(dataTest),
    [DataTestType.ReferentialIntegrity]: (dataTest) => this.referentialIntegrityFormatter(dataTest),
  };

  public getFormattedText(
    dataTest$: Observable<DataTest | null | undefined>,
    schemaTest$: Observable<SchemaTestForm | null | undefined>,
  ): Observable<string> {
    return combineLatest([dataTest$, schemaTest$]).pipe(
      map(([dataTest, schemaTest]): string => {
        if (isNullish(dataTest)) {
          return '';
        }
        return this.conditionTextFormatterDef[dataTest.type]?.(dataTest, schemaTest) ?? '';
      }),
    );
  }

  private schemaFormatter(dataTest: DataTest) {
    if (dataTest.type !== DataTestType.Schema) {
      return '';
    }
    return `Additional columns ${dataTest.allow_new_columns ? '' : 'not'} allowed`;
  }

  private rowDriftFormatter(dataTest: DataTest): string {
    if (dataTest.type !== DataTestType.NumRowDrift) {
      return '';
    }
    const { upper_threshold, lower_threshold, threshold_type } = dataTest.row_change;
    // constructs "± 5.5x amount of rows allowed", "+40, -30 rows are allowed
    const result: string[] = [];
    if (lower_threshold === upper_threshold) {
      result.push(`±${upper_threshold}${threshold_type === 'relative' ? 'x' : ''}`);
    } else {
      result.push(upper_threshold ? `+${upper_threshold}${threshold_type === 'relative' ? 'x' : ''}, ` : '');
      result.push(lower_threshold ? `-${lower_threshold}${threshold_type === 'relative' ? 'x' : ''}` : '');
    }
    result.push(' amount of rows allowed');

    return result.join('');
  }

  private lowCodeFormatter(dataTest: DataTest) {
    if (dataTest.type !== DataTestType.LowCode) {
      return '';
    }
    return dataTest.low_code_string;
  }

  private uniqueFormatter(dataTest: DataTest) {
    if (dataTest.type !== DataTestType.Unique) {
      return '';
    }
    return `NULL values ${dataTest.allow_null_values ? '' : 'not'} allowed`;
  }

  private referentialIntegrityFormatter(dataTest: DataTest) {
    if (dataTest.type !== DataTestType.ReferentialIntegrity) {
      return '';
    }

    return `Values in '${dataTest.column}' have to be present in '${dataTest.foreign_table_column}' of '${dataTest.foreign_table_path}'`;
  }

  private columnFormatter(dataTest: DataTest, schemaTest?: SchemaTestForm | null) {
    if (dataTest.type !== DataTestType.Column || isNullish(schemaTest)) {
      return '-';
    }

    const columnInSchema = schemaTest.schema.find((column) => column.column === dataTest.column);
    if (isNullish(columnInSchema)) {
      console.warn(`[Data-test] No column "${dataTest.column}" found in schema test`);
      return '-';
    }

    return this.operationService.constructDisplayValue(dataTest.column_conditions.operations, columnInSchema.type);
  }
}
